import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AccountTileIcon from '../ui/AccountTileIcon';
import AccountAddressBook from './AccountAddressBook';
import AccountCard from './AccountCard';
import AccountCustomerBespokeRequests from './AccountCustomerBespokeRequests';
import {
  AccountCustomerOrders,
  AccountCustomerProfile,
  AccountOrdersOverviewList,
} from './AccountCustomerPanels';
import { isOrderCancelled } from './orderStatusLabel';
import PasswordInput from '../components/PasswordInput';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'orders', label: 'Orders' },
  { id: 'custom-designs', label: 'Custom designs' },
  { id: 'addresses', label: 'Addresses' },
  { id: 'profile', label: 'Profile' },
  { id: 'security', label: 'Security' },
];

function profileInitial(name, email) {
  const src = (name || email || '?').trim();
  return src.charAt(0).toUpperCase();
}

function OverviewStat({ label, value, hint, ...rest }) {
  return (
    <div className="customer-account__stat" {...rest}>
      <span className="customer-account__stat-value">{value}</span>
      <span className="customer-account__stat-label">{label}</span>
      {hint ? <span className="customer-account__stat-hint">{hint}</span> : null}
    </div>
  );
}

function QuickAction({ to, href, icon, title, desc, external }) {
  const className = 'customer-account__action';
  const inner = (
    <>
      <span className="customer-account__action-icon" aria-hidden="true">
        <AccountTileIcon name={icon} />
      </span>
      <span className="customer-account__action-text">
        <span className="customer-account__action-title">{title}</span>
        <span className="customer-account__action-desc">{desc}</span>
      </span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        className={className}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {inner}
    </Link>
  );
}

export function CustomerAccountPassword({ changePassword }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const res = await changePassword(currentPassword, newPassword, confirmPassword);
      if (!res.ok) {
        setError(res.error || 'Could not update password.');
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Password updated. Use your new password next time you sign in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="customer-account__form">
      {error ? (
        <div className="alert alert-danger py-2 small" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="alert alert-success py-2 small" role="status">
          {success}
        </div>
      ) : null}
      <div className="checkout-field">
        <label className="checkout-field__label" htmlFor="hub-current-password">
          Current password
        </label>
        <PasswordInput
          id="hub-current-password"
          className="form-control checkout-input"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={busy}
          required
        />
      </div>
      <div className="checkout-field">
        <label className="checkout-field__label" htmlFor="hub-new-password">
          New password
        </label>
        <PasswordInput
          id="hub-new-password"
          className="form-control checkout-input"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={busy}
          minLength={8}
          required
        />
      </div>
      <div className="checkout-field">
        <label className="checkout-field__label" htmlFor="hub-confirm-password">
          Confirm new password
        </label>
        <PasswordInput
          id="hub-confirm-password"
          className="form-control checkout-input"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={busy}
          minLength={8}
          required
        />
      </div>
      <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={busy}>
        {busy ? 'Updating…' : 'Update password'}
      </button>
    </form>
  );
}

export default function CustomerAccountHub({
  displayName,
  email,
  phone,
  wishlistCount,
  orderCount,
  site,
  logout,
  changePassword,
  updateProfile,
  listOrders,
  getOrderDetail,
  addItem,
  products,
}) {
  const [searchParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const [tab, setTab] = useState(initialTab);
  const [overviewOrders, setOverviewOrders] = useState([]);
  const [overviewOrdersLoading, setOverviewOrdersLoading] = useState(false);
  const shownName = (displayName || email || 'Guest').trim();
  const emailShown = (email || '').trim();

  useEffect(() => {
    const q = searchParams.get('tab');
    if (q && TABS.some((t) => t.id === q)) setTab(q);
  }, [searchParams]);

  useEffect(() => {
    if (tab !== 'overview' || !listOrders) return undefined;
    let cancelled = false;
    setOverviewOrdersLoading(true);
    listOrders()
      .then((res) => {
        if (cancelled) return;
        setOverviewOrdersLoading(false);
        if (res.ok && Array.isArray(res.orders)) setOverviewOrders(res.orders);
        else setOverviewOrders([]);
      })
      .catch(() => {
        if (cancelled) return;
        setOverviewOrdersLoading(false);
        setOverviewOrders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, listOrders]);

  const orderLabel = orderCount === null ? '—' : orderCount === 1 ? '1' : String(orderCount);
  const cancelledOverviewCount = overviewOrders.filter((o) => isOrderCancelled(o)).length;
  const wishLabel = wishlistCount > 0 ? String(wishlistCount) : '0';

  return (
    <div className="customer-account">
      <header className="customer-account__welcome">
        <div className="customer-account__identity">
          <span className="customer-account__avatar" aria-hidden="true">
            {profileInitial(shownName, emailShown)}
          </span>
          <div className="customer-account__who">
            <p className="customer-account__eyebrow">Welcome back</p>
            <h1 className="customer-account__name">{shownName}</h1>
            {emailShown ? <p className="customer-account__email">{emailShown}</p> : null}
          </div>
        </div>
        <div className="customer-account__header-actions">
          <Link to="/shop" className="btn btn-primary btn-sm rounded-pill px-3">
            Continue shopping
          </Link>
          <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>

      <nav className="customer-account__nav" aria-label="Account sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`customer-account__nav-btn${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="customer-account__panel">
        {tab === 'overview' ? (
          <div className="customer-account__overview">
            <div className="customer-account__stats" role="list">
              <OverviewStat
                role="listitem"
                label="Orders"
                value={orderLabel}
                hint={
                  orderCount === 0
                    ? 'Start with the shop'
                    : cancelledOverviewCount > 0
                      ? `Includes ${cancelledOverviewCount} cancelled`
                      : 'View in Orders tab'
                }
              />
              <OverviewStat role="listitem" label="Saved items" value={wishLabel} hint="Your wishlist" />
              <OverviewStat
                role="listitem"
                label="Profile"
                value={phone ? '✓' : '—'}
                hint={phone ? 'Phone on file' : 'Add phone in Profile'}
              />
            </div>

            {orderCount > 0 ? (
              <AccountCard
                id="overview-orders"
                title="Recent orders"
                lead="Including cancelled orders — open Orders for full details and receipts."
              >
                <AccountOrdersOverviewList
                  orders={overviewOrders}
                  loading={overviewOrdersLoading}
                  onViewAll={() => setTab('orders')}
                />
              </AccountCard>
            ) : null}

            <AccountCard id="overview-shop" title="Shop faster" lead="Shortcuts to cart, saved pieces, and order tracking.">
              <div className="customer-account__actions">
                <QuickAction to="/cart" icon="cart" title="Your bag" desc="Review & checkout" />
                <QuickAction
                  to="/wishlist"
                  icon="heart"
                  title="Saved items"
                  desc={wishlistCount > 0 ? `${wishlistCount} waiting for you` : 'Heart pieces you love'}
                />
                <QuickAction to="/track-order" icon="package" title="Track order" desc="Lookup by order number" />
                <QuickAction to="/custom-jewelry" icon="sparkle" title="Custom jewelry" desc="Request a bespoke design" />
                <QuickAction
                  to="/account?tab=custom-designs"
                  icon="sparkle"
                  title="My custom requests"
                  desc="Status of design enquiries"
                />
                <QuickAction to="/shop" icon="shop" title="Browse shop" desc="Rakhi, Lumba & more" />
                <QuickAction
                  href={site?.whatsapp_href || 'https://wa.me/919680384332'}
                  icon="message"
                  title="WhatsApp"
                  desc="Custom orders & timing"
                  external
                />
                <QuickAction to="/contact" icon="message" title="Contact" desc="Email or message us" />
              </div>
              {orderCount > 0 ? (
                <p className="customer-account__overview-tip">
                  <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setTab('orders')}>
                    View all orders
                  </button>
                  {' · '}
                  <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setTab('addresses')}>
                    Manage addresses
                  </button>
                </p>
              ) : null}
            </AccountCard>

            <AccountCard id="overview-help" title="Need help?" className="customer-account__help-card">
              <p className="mb-3">
                Questions about delivery, customization, or festival timing? We are happy to help on WhatsApp or through
                our contact form.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <a
                  href={site?.whatsapp_href || 'https://wa.me/919680384332'}
                  className="btn btn-outline-primary btn-sm rounded-pill"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Chat on WhatsApp
                </a>
                <Link to="/contact" className="btn btn-outline-secondary btn-sm rounded-pill">
                  Contact page
                </Link>
              </div>
            </AccountCard>
          </div>
        ) : null}

        {tab === 'orders' ? (
          <AccountCard id="orders" title="Your orders" lead="Track status, reorder, or request cancellation.">
            <AccountCustomerOrders
              embedded
              listOrders={listOrders}
              getOrderDetail={getOrderDetail}
              addItem={addItem}
              products={products}
            />
          </AccountCard>
        ) : null}

        {tab === 'custom-designs' ? (
          <AccountCard
            id="custom-designs"
            title="Custom jewelry requests"
            lead="Bespoke design enquiries from /custom-jewelry — status and photos you submitted."
          >
            <AccountCustomerBespokeRequests embedded />
          </AccountCard>
        ) : null}

        {tab === 'addresses' ? (
          <AccountCard id="addresses" title="Delivery addresses" lead="Save addresses for faster checkout across India.">
            <AccountAddressBook embedded />
          </AccountCard>
        ) : null}

        {tab === 'profile' ? (
          <AccountCard id="profile" title="Your details" lead="Used for receipts and delivery updates.">
            <AccountCustomerProfile
              embedded
              displayName={displayName}
              email={email}
              phone={phone}
              updateProfile={updateProfile}
            />
          </AccountCard>
        ) : null}

        {tab === 'security' ? (
          <AccountCard
            id="security"
            title="Password"
            lead="Choose a strong password (at least 8 characters). You stay signed in on this device after updating."
          >
            <CustomerAccountPassword changePassword={changePassword} />
          </AccountCard>
        ) : null}
      </div>
    </div>
  );
}
